import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NgChartsModule } from 'ng2-charts';
import { ChartConfiguration, ChartOptions } from 'chart.js';
import { ApiService } from '@/app/modules/dashboard/infra/api.service';
import { ChecklistHeaderService } from '@/app/modules/checklist-header/core/service';
import { HeaderTypeEnum } from '@/app/modules/checklist-header/core/type';
import { BehaviorSubject, Subject, Observable } from 'rxjs';
import { switchMap, takeUntil, map } from 'rxjs/operators';

@Component({
  selector: 'app-maintenance-chart',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzSelectModule,
    NzCardModule,
    NzSkeletonModule,
    NgChartsModule
  ],
  templateUrl: './maintenance-chart.component.html',
  styleUrl: './maintenance-chart.component.scss'
})
export class MaintenanceChartComponent implements OnInit, OnDestroy {
  @Input() set chassis(value: string | null) {
    this.chassisSubject.next(value);
  }
  
  public chassisList: string[] = [];
  public loading = true;
  
  private destroy$ = new Subject<void>();
  private chassisSubject = new BehaviorSubject<string | null>(null);

  public chartData?: ChartConfiguration['data'];
  public chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: { 
        beginAtZero: true, 
        grid: { display: false },
        ticks: {
          precision: 0
        }
      },
      x: { grid: { display: false } }
    },
    plugins: {
      legend: { position: 'bottom' },
      tooltip: { mode: 'index', intersect: false }
    }
  };

  constructor(
    private apiService: ApiService,
    private checklistHeaderService: ChecklistHeaderService
  ) {}

  ngOnInit(): void {
    this.loadChassisList().subscribe();
    this.setupChartSubscription();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public loadChassisList(): Observable<string[]> {
    return this.checklistHeaderService.findAll({ 
      type: HeaderTypeEnum.MAINTENANCE, 
      pageIndex: 1,
      pageSize: 1000 
    }).pipe(
      map(response => {
        const uniqueChassis = new Set(response.items.map(i => i.chassis).filter(Boolean));
        const list = Array.from(uniqueChassis as Set<string>).sort();
        this.chassisList = list;
        return list;
      })
    );
  }

  private setupChartSubscription(): void {
    this.chassisSubject.pipe(
      takeUntil(this.destroy$),
      switchMap(chassis => {
        this.loading = true;
        return this.apiService.getMaintenanceAnalytics(chassis || undefined);
      })
    ).subscribe({
      next: (data) => {
        this.chartData = {
          labels: data.labels,
          datasets: data.datasets.map(ds => ({
            ...ds,
            borderWidth: 1,
            borderRadius: 5
          }))
        };
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }
}
